/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { Repository } from "@octopusdeploy/octopus-server-client";
import type { EnvironmentMachinesArgs, EnvironmentSummaryArgs, MachineModelHealthStatus } from "@octopusdeploy/octopus-server-client";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import type { CustomFieldIndex } from "~/components/tenantCustomFields";
import { getTenantCustomFieldIndex, getEnvironmentCustomFieldIndex } from "~/components/tenantCustomFields";
import type { TagIndex } from "~/components/tenantTagsets";
import type { BaseInfrastructureFilter } from "../MachinesLayout/MachineFilter";
import { defaultBaseInfrastructureFilter } from "../MachinesLayout/MachineFilter";
export interface EnvironmentSummaryFilter extends BaseInfrastructureFilter {
    environmentIds: string[];
    roles: string[];
    tenantIds: string[];
    tenantTags: string[];
    tenantCustomFields: string[];
    environmentCustomFields: string[];
    shellNames: string[];
    deploymentTargetTypes: string[];
    hideEmptyEnvironments: boolean;
    machinePartialName: string | undefined;
}
export const defaultEnvironmentSummaryFilter: EnvironmentSummaryFilter = {
    ...defaultBaseInfrastructureFilter,
    environmentIds: [],
    roles: [],
    tenantIds: [],
    tenantTags: [],
    tenantCustomFields: [],
    environmentCustomFields: [],
    shellNames: [],
    deploymentTargetTypes: [],
    hideEmptyEnvironments: false,
    machinePartialName: undefined,
};
export const createEnvironmentMachinesArgs = (filter: EnvironmentSummaryFilter, isDisabled: boolean | undefined, applicableHealthStatusFilters: MachineModelHealthStatus[]): EnvironmentMachinesArgs => {
    const skip = 0; // Don't skip, just increase the take size.
    const take = Repository.takeAll; // No paging, just take all, because we need to group our response data by health status.
    // Here we take the machinePartialName from the filter, as this is where we bind the worker name from the UI.
    // We map this to partialName on the returned args, as this is where the API expects the worker name for the query.
    const partialName = filter.machinePartialName;
    const roles = filter.roles ? filter.roles.join(",") : null;
    const healthStatuses = applicableHealthStatusFilters ? applicableHealthStatusFilters.join(",") : null;
    const tenantIds = filter.tenantIds.length > 0 ? filter.tenantIds.join(",") : null;
    const tenantTags = filter.tenantTags.length > 0 ? filter.tenantTags.join(",") : null;
    const shellNames = filter.shellNames ? filter.shellNames.join(",") : null;
    const deploymentTargetTypes = filter.deploymentTargetTypes ? filter.deploymentTargetTypes.join(",") : null;
    return { skip, take, partialName, roles, isDisabled, healthStatuses, tenantIds, tenantTags, shellNames, deploymentTargetTypes };
};
export const createEnvironmentSummaryArgs = async (filter: EnvironmentSummaryFilter, tagIndex?: TagIndex, customFieldIndex?: CustomFieldIndex): Promise<Partial<EnvironmentSummaryArgs>> => {
    const customFieldsAreEnabled = isFeatureToggleEnabled("ExtendedTagSetsFeatureToggle");
    const args: Partial<EnvironmentSummaryArgs> = {};
    if (filter.partialName && filter.partialName !== "") {
        args.partialName = filter.partialName;
    }
    if (filter.machinePartialName && filter.machinePartialName !== "") {
        args.machinePartialName = filter.machinePartialName;
    }
    if (filter.roles && filter.roles.length) {
        args.roles = filter.roles.join(",");
    }
    if (filter.shellNames && filter.shellNames.length) {
        args.shellNames = filter.shellNames.join(",");
    }
    if (filter.deploymentTargetTypes && filter.deploymentTargetTypes.length) {
        args.deploymentTargetTypes = filter.deploymentTargetTypes.join(",");
    }
    if (filter.isDisabled) {
        args.isDisabled = true;
    }
    if (filter.environmentIds && filter.environmentIds.length) {
        args.ids = filter.environmentIds.join(",");
    }
    if (filter.healthStatuses && filter.healthStatuses.length) {
        args.healthStatuses = filter.healthStatuses.join(",");
    }
    if (filter.tenantIds && filter.tenantIds.length) {
        args.tenantIds = filter.tenantIds.join(",");
    }
    if (filter.tenantTags && filter.tenantTags.length && tagIndex) {
        args.tenantTags = filter.tenantTags.map((name) => tagIndex[name].Id).join(",");
    }
    if (customFieldsAreEnabled && filter.tenantCustomFields && filter.tenantCustomFields.length && customFieldIndex) {
        const [tenantCustomFieldIndex, environmentCustomFieldIndex] = await Promise.all([getTenantCustomFieldIndex(), getEnvironmentCustomFieldIndex()]);
        const tenantCustomFields: string[] = [];
        const environmentCustomFields: string[] = [];
        filter.tenantCustomFields.forEach((fieldName) => {
            if (tenantCustomFieldIndex[fieldName]) {
                tenantCustomFields.push(tenantCustomFieldIndex[fieldName].Id);
            }
            else if (environmentCustomFieldIndex[fieldName]) {
                environmentCustomFields.push(environmentCustomFieldIndex[fieldName].Id);
            }
        });
        if (tenantCustomFields.length > 0) {
            args.tenantCustomFieldValue = tenantCustomFields.join(",");
        }
        if (environmentCustomFields.length > 0) {
            args.environmentCustomFieldValue = environmentCustomFields.join(",");
        }
    }
    if (customFieldsAreEnabled && filter.environmentCustomFields && filter.environmentCustomFields.length) {
        const environmentCustomFieldIndex = await getEnvironmentCustomFieldIndex();
        const environmentCustomFields = filter.environmentCustomFields.map((fieldName) => environmentCustomFieldIndex[fieldName]?.Id).filter(Boolean);
        if (environmentCustomFields.length > 0) {
            const existingValue = args.environmentCustomFieldValue;
            const combinedValues = existingValue ? `${existingValue},${environmentCustomFields.join(",")}` : environmentCustomFields.join(",");
            args.environmentCustomFieldValue = combinedValues;
        }
    }
    if (filter.hideEmptyEnvironments) {
        args.hideEmptyEnvironments = true;
    }
    return args;
};
